import { FC, useRef, useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Box,
    Typography,
    Slider,
    IconButton,
    Tooltip,
    TextField,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Alert,
} from '@mui/material';
import {
    Close,
    Brush,
    Undo,
    Delete,
    Visibility,
    VisibilityOff,
    AddPhotoAlternate,
    Image as ImageIcon,
    PanTool,
    ZoomIn,
    ZoomOut,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../../../../../atomic/atoms';

interface ReferenceImage {
    base64: string; // Pure base64 data (without data:xxx;base64, prefix)
    mimeType: string;
    preview: string; // Data URL for preview
}

interface InpaintingModalProps {
    open: boolean;
    imageUrl: string;
    onClose: () => void;
    onGenerate: (maskBase64: string, prompt: string, referenceImage: ReferenceImage) => void;
    isLoading?: boolean;
}

interface DrawPoint {
    x: number; // Image-space coordinate
    y: number; // Image-space coordinate
}

interface DrawStroke {
    points: DrawPoint[];
    brushSize: number; // Image-space brush size
    isErase?: boolean;
}

export const InpaintingModal: FC<InpaintingModalProps> = ({
    open,
    imageUrl,
    onClose,
    onGenerate,
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const maskCanvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [brushSize, setBrushSize] = useState(30); // Screen pixels
    const [isDrawing, setIsDrawing] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [showMask, setShowMask] = useState(true);
    const [strokes, setStrokes] = useState<DrawStroke[]>([]);
    const [currentStroke, setCurrentStroke] = useState<DrawStroke | null>(null);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [brushMode, setBrushMode] = useState<'draw' | 'erase' | 'grab'>('draw');
    const [referenceImage, setReferenceImage] = useState<ReferenceImage | null>(null);

    // Zoom & Pan state
    const [zoom, setZoom] = useState(1);
    const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const panStartRef = useRef({ clientX: 0, clientY: 0, panX: 0, panY: 0 });

    const imageRef = useRef<HTMLImageElement | null>(null);
    const blobUrlRef = useRef<string | null>(null);
    const referenceInputRef = useRef<HTMLInputElement>(null);

    // Internal rendering refs — computed once when image loads or container resizes
    const baseScaleRef = useRef(1); // Scale to fit image inside canvas at zoom=1
    const displaySizeRef = useRef({ w: 0, h: 0 }); // Canvas CSS size in px

    // Load image when modal opens
    useEffect(() => {
        if (!open || !imageUrl) return;

        setImageLoaded(false);
        setImageError(false);
        setZoom(1);
        setPanOffset({ x: 0, y: 0 });
        setIsPanning(false);

        // Clean up previous blob URL
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }

        // Helper: load image directly without crossOrigin (works for all servers)
        const loadImageDirect = (url: string) => {
            const img = new Image();
            img.onload = () => {
                imageRef.current = img;
                setImageLoaded(true);
            };
            img.onerror = () => {
                console.error('Failed to load image for inpainting (direct)');
                setImageError(true);
            };
            img.src = url;
        };

        // Try fetch as blob first (avoids canvas tainting for future use)
        fetch(imageUrl, { mode: 'cors' })
            .then((res) => {
                if (!res.ok) throw new Error('Fetch failed');
                return res.blob();
            })
            .then((blob) => {
                const blobUrl = URL.createObjectURL(blob);
                blobUrlRef.current = blobUrl;
                const img = new Image();
                img.onload = () => {
                    imageRef.current = img;
                    setImageLoaded(true);
                };
                img.onerror = () => {
                    // Fallback to direct load
                    loadImageDirect(imageUrl);
                };
                img.src = blobUrl;
            })
            .catch(() => {
                // Fallback to direct load without crossOrigin
                loadImageDirect(imageUrl);
            });

        return () => {
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
            imageRef.current = null;
            setImageLoaded(false);
            setImageError(false);
            setStrokes([]);
            setCurrentStroke(null);
            setPrompt('');
            setReferenceImage(null);
            setZoom(1);
            setPanOffset({ x: 0, y: 0 });
            setIsPanning(false);
        };
    }, [open, imageUrl]);

    // Setup canvas dimensions to match container (runs once on image load + on resize)
    useEffect(() => {
        if (!imageLoaded || !canvasRef.current || !maskCanvasRef.current || !containerRef.current) return;

        const container = containerRef.current;
        const img = imageRef.current!;
        const dpr = window.devicePixelRatio || 1;

        const displayWidth = container.clientWidth;
        const displayHeight = container.clientHeight || 600;

        // Base scale = fit image in container at zoom=1
        const bs = Math.min(displayWidth / img.naturalWidth, displayHeight / img.naturalHeight);
        baseScaleRef.current = bs;
        displaySizeRef.current = { w: displayWidth, h: displayHeight };

        // High-DPI canvas buffer for sharp rendering
        canvasRef.current.width = Math.round(displayWidth * dpr);
        canvasRef.current.height = Math.round(displayHeight * dpr);
        canvasRef.current.style.width = `${displayWidth}px`;
        canvasRef.current.style.height = `${displayHeight}px`;

        // Mask canvas at original image resolution
        maskCanvasRef.current.width = img.naturalWidth;
        maskCanvasRef.current.height = img.naturalHeight;
    }, [imageLoaded]);

    // ----- Coordinate conversion: screen → image space -----

    const getImageCoords = useCallback(
        (clientX: number, clientY: number) => {
            const canvas = canvasRef.current;
            const img = imageRef.current;
            if (!canvas || !img) return { x: 0, y: 0 };

            const rect = canvas.getBoundingClientRect();
            // Mouse position in CSS-pixel canvas space
            const canvasX = clientX - rect.left;
            const canvasY = clientY - rect.top;

            const bs = baseScaleRef.current;
            const { w, h } = displaySizeRef.current;
            const s = bs * zoom;

            // Reverse transform: translate(w/2 + panX, h/2 + panY) → scale(s) → translate(-nw/2, -nh/2)
            const imgX = (canvasX - w / 2 - panOffset.x) / s + img.naturalWidth / 2;
            const imgY = (canvasY - h / 2 - panOffset.y) / s + img.naturalHeight / 2;

            return { x: imgX, y: imgY };
        },
        [zoom, panOffset]
    );

    // ----- Drawing helpers (image-space) -----

    const drawStrokeOnCtx = (ctx: CanvasRenderingContext2D, stroke: DrawStroke, color: string) => {
        if (stroke.points.length === 1) {
            ctx.beginPath();
            ctx.arc(stroke.points[0].x, stroke.points[0].y, stroke.brushSize / 2, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
        } else {
            ctx.beginPath();
            ctx.lineWidth = stroke.brushSize;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.strokeStyle = color;
            ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
            for (let i = 1; i < stroke.points.length; i++) {
                ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
            }
            ctx.stroke();
        }
    };

    // ----- Canvas redraw (zoom & pan handled internally, NOT via CSS) -----

    const redrawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        const img = imageRef.current;
        if (!canvas || !ctx || !img) return;

        const dpr = window.devicePixelRatio || 1;
        const { w, h } = displaySizeRef.current;
        const bs = baseScaleRef.current;
        const s = bs * zoom;

        // Reset transform and clear
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Apply: HiDPI → translate(center + pan) → scale(baseScale * zoom) → translate(-imageCenter)
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.translate(w / 2 + panOffset.x, h / 2 + panOffset.y);
        ctx.scale(s, s);
        ctx.translate(-img.naturalWidth / 2, -img.naturalHeight / 2);

        // Draw image at full natural resolution → canvas renders the visible portion sharply
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0);

        // Draw mask overlay in image space
        if (showMask) {
            const allStrokes = currentStroke ? [...strokes, currentStroke] : strokes;

            if (allStrokes.length > 0) {
                // Off-screen canvas at image resolution for compositing (draw + erase order)
                const overlay = document.createElement('canvas');
                overlay.width = img.naturalWidth;
                overlay.height = img.naturalHeight;
                const oCtx = overlay.getContext('2d')!;

                for (const stroke of allStrokes) {
                    if (stroke.isErase) {
                        oCtx.globalCompositeOperation = 'destination-out';
                        drawStrokeOnCtx(oCtx, stroke, 'white');
                    } else {
                        oCtx.globalCompositeOperation = 'source-over';
                        drawStrokeOnCtx(oCtx, stroke, 'rgba(255, 50, 50, 0.45)');
                    }
                }

                // Draw overlay onto main canvas (still in image-space transform)
                ctx.drawImage(overlay, 0, 0);
            }
        }

        // Reset transform
        ctx.setTransform(1, 0, 0, 1, 0, 0);
    }, [strokes, currentStroke, showMask, zoom, panOffset, imageLoaded]);

    // Trigger redraw on any visual change
    useEffect(() => {
        redrawCanvas();
    }, [redrawCanvas]);

    // -- Mouse handlers --

    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (isLoading) return;

        // Grab mode: start panning
        if (brushMode === 'grab') {
            setIsPanning(true);
            panStartRef.current = {
                clientX: e.clientX,
                clientY: e.clientY,
                panX: panOffset.x,
                panY: panOffset.y,
            };
            return;
        }

        // Draw/Erase mode — convert to image space
        const coords = getImageCoords(e.clientX, e.clientY);
        const bs = baseScaleRef.current;
        const brushSizeInImageSpace = brushSize / (bs * zoom);

        setIsDrawing(true);
        const newStroke: DrawStroke = {
            points: [coords],
            brushSize: brushSizeInImageSpace,
            isErase: brushMode === 'erase',
        };
        setCurrentStroke(newStroke);
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        // Grab mode panning is handled via document listeners
        if (brushMode === 'grab') return;
        if (!isDrawing || !currentStroke || isLoading) return;
        const coords = getImageCoords(e.clientX, e.clientY);

        setCurrentStroke((prev) => {
            if (!prev) return null;
            return {
                ...prev,
                points: [...prev.points, coords],
            };
        });
    };

    const handleMouseUp = () => {
        // Grab mode panning is handled via document listeners
        if (brushMode === 'grab') return;
        if (!isDrawing || !currentStroke) return;
        setIsDrawing(false);
        setStrokes((prev) => [...prev, currentStroke]);
        setCurrentStroke(null);
    };

    // Document-level listeners for smooth panning (continues even if cursor leaves canvas)
    useEffect(() => {
        if (!isPanning) return;

        const handleMove = (e: MouseEvent) => {
            const dx = e.clientX - panStartRef.current.clientX;
            const dy = e.clientY - panStartRef.current.clientY;
            setPanOffset({
                x: panStartRef.current.panX + dx,
                y: panStartRef.current.panY + dy,
            });
        };

        const handleUp = () => {
            setIsPanning(false);
        };

        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleUp);

        return () => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
        };
    }, [isPanning]);

    // Wheel zoom on canvas container — centered on mouse position
    useEffect(() => {
        const container = containerRef.current;
        if (!container || !imageLoaded) return;

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.15 : 0.15;

            setZoom((prevZoom) => {
                const newZoom = Math.min(5, Math.max(1, +(prevZoom + delta).toFixed(2)));
                if (newZoom === prevZoom) return prevZoom;

                // Adjust pan so the point under the mouse stays fixed
                const canvas = canvasRef.current;
                if (canvas) {
                    const rect = canvas.getBoundingClientRect();
                    const mx = e.clientX - rect.left; // Mouse X in canvas CSS space
                    const my = e.clientY - rect.top;
                    const { w, h } = displaySizeRef.current;
                    const ratio = newZoom / prevZoom;

                    setPanOffset((prev) => ({
                        x: (1 - ratio) * (mx - w / 2) + ratio * prev.x,
                        y: (1 - ratio) * (my - h / 2) + ratio * prev.y,
                    }));
                }

                return newZoom;
            });
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => container.removeEventListener('wheel', handleWheel);
    }, [imageLoaded]);

    // Double-click to reset zoom & pan
    const handleDoubleClick = () => {
        setZoom(1);
        setPanOffset({ x: 0, y: 0 });
    };

    const handleUndo = () => {
        setStrokes((prev) => prev.slice(0, -1));
    };

    const handleClear = () => {
        setStrokes([]);
        setCurrentStroke(null);
    };

    // ----- Mask generation (strokes are already in image space → no scaling needed) -----

    const generateMask = (): string | null => {
        const maskCanvas = maskCanvasRef.current;
        const img = imageRef.current;
        if (!maskCanvas || !img || strokes.length === 0) return null;

        const ctx = maskCanvas.getContext('2d')!;

        // Fill with black (areas to keep)
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = 'black';
        ctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

        // Draw strokes directly in image space (mask is at image resolution)
        for (const stroke of strokes) {
            if (stroke.isErase) {
                ctx.globalCompositeOperation = 'destination-out';
                drawStrokeOnCtx(ctx, stroke, 'white');
            } else {
                ctx.globalCompositeOperation = 'source-over';
                drawStrokeOnCtx(ctx, stroke, 'white');
            }
        }

        return maskCanvas.toDataURL('image/png');
    };

    const handleReferenceImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) return;

        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result as string;
            const base64Data = dataUrl.split(',')[1];
            const mimeType = file.type;
            setReferenceImage({
                base64: base64Data,
                mimeType,
                preview: dataUrl,
            });
        };
        reader.readAsDataURL(file);

        // Reset input to allow re-selecting same file
        e.target.value = '';
    };

    const handleGenerate = () => {
        const maskDataUrl = generateMask();
        if (!maskDataUrl || !referenceImage) return;

        // Extract base64 data
        const base64 = maskDataUrl.split(',')[1];
        onGenerate(base64, prompt, referenceImage);
    };

    const hasMask = strokes.length > 0;
    const hasReference = referenceImage !== null;

    // Cursor based on mode — size in screen pixels (matches visual brush)
    const getCanvasCursor = () => {
        if (isLoading) return 'not-allowed';
        if (brushMode === 'grab') return isPanning ? 'grabbing' : 'grab';
        const screenBrush = brushSize; // Brush slider = screen pixels
        const color = brushMode === 'draw' ? '%23ff5050' : '%2390caf9';
        return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${screenBrush}' height='${screenBrush}'%3E%3Ccircle cx='${screenBrush / 2}' cy='${screenBrush / 2}' r='${screenBrush / 2 - 1}' fill='none' stroke='${color}' stroke-width='2'/%3E%3C/svg%3E") ${screenBrush / 2} ${screenBrush / 2}, crosshair`;
    };

    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            maxWidth="lg"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        height: '90vh',
                        maxHeight: '90vh',
                        bgcolor: '#1a1a2e',
                        color: 'white',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    py: 1.5,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Brush sx={{ color: '#90caf9' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '16px' }}>
                        {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.title')}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={isLoading} sx={{ color: 'white' }}>
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, overflow: 'hidden' }}>
                {/* Toolbar */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        borderRadius: 2,
                        flexWrap: 'wrap',
                    }}
                >
                    {/* Draw / Erase / Grab toggle */}
                    <ToggleButtonGroup
                        value={brushMode}
                        exclusive
                        onChange={(_, val) => {
                            if (!val) return;
                            setBrushMode(val);
                            if (val !== 'grab') setIsPanning(false);
                        }}
                        size="small"
                        sx={{
                            '& .MuiToggleButton-root': {
                                color: 'rgba(255,255,255,0.6)',
                                borderColor: 'rgba(255,255,255,0.2)',
                                '&.Mui-selected': {
                                    bgcolor: 'rgba(144,202,249,0.2)',
                                    color: '#90caf9',
                                },
                            },
                        }}
                    >
                        <ToggleButton value="draw">
                            <Tooltip title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.draw')}>
                                <Brush fontSize="small" />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="erase">
                            <Tooltip title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.erase')}>
                                <Delete fontSize="small" />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="grab">
                            <Tooltip title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.grab')}>
                                <PanTool fontSize="small" />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* Brush size - only visible when not in grab mode */}
                    {brushMode !== 'grab' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap' }}>
                                {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.brushSize')}
                            </Typography>
                            <Slider
                                value={brushSize}
                                onChange={(_, val) => setBrushSize(val as number)}
                                min={5}
                                max={100}
                                size="small"
                                sx={{
                                    width: 120,
                                    color: '#90caf9',
                                    '& .MuiSlider-thumb': { width: 14, height: 14 },
                                }}
                            />
                            <Typography variant="caption" sx={{ color: '#90caf9', minWidth: 24, textAlign: 'center' }}>
                                {brushSize}
                            </Typography>
                        </Box>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.undo')}>
                            <span>
                                <IconButton
                                    onClick={handleUndo}
                                    disabled={strokes.length === 0 || isLoading}
                                    size="small"
                                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                                >
                                    <Undo fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.clear')}>
                            <span>
                                <IconButton
                                    onClick={handleClear}
                                    disabled={strokes.length === 0 || isLoading}
                                    size="small"
                                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip
                            title={
                                showMask
                                    ? t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.hideMask')
                                    : t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.showMask')
                            }
                        >
                            <IconButton
                                onClick={() => setShowMask(!showMask)}
                                size="small"
                                sx={{ color: showMask ? '#90caf9' : 'rgba(255,255,255,0.4)' }}
                            >
                                {showMask ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Zoom info in toolbar */}
                    {zoom > 1 && (
                        <Tooltip title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.resetZoom')}>
                            <Typography
                                variant="caption"
                                onClick={handleDoubleClick}
                                sx={{
                                    color: '#90caf9',
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(144,202,249,0.15)',
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 1,
                                    fontWeight: 600,
                                    fontSize: '11px',
                                    '&:hover': { bgcolor: 'rgba(144,202,249,0.25)' },
                                }}
                            >
                                {Math.round(zoom * 100)}% ✕
                            </Typography>
                        </Tooltip>
                    )}
                </Box>

                {/* Canvas Area + Zoom Slider */}
                <Box sx={{ display: 'flex', flex: 1, gap: 1, overflow: 'hidden', minHeight: 300 }}>
                    {/* Canvas Container */}
                    <Box
                        ref={containerRef}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(0,0,0,0.3)',
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        {imageError ? (
                            <Box sx={{ textAlign: 'center', p: 3 }}>
                                <Alert
                                    severity="error"
                                    sx={{ mb: 2, bgcolor: 'rgba(211,47,47,0.15)', color: '#ff8a80' }}
                                >
                                    {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.imageLoadError')}
                                </Alert>
                                <SCUIButton
                                    type="secondary"
                                    size="m"
                                    sizeType="default"
                                    text={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.retry')}
                                    onClick={() => {
                                        setImageError(false);
                                        setImageLoaded(false);
                                        // Re-trigger loading by re-mounting effect
                                        const img = new Image();
                                        img.onload = () => {
                                            imageRef.current = img;
                                            setImageLoaded(true);
                                        };
                                        img.onerror = () => setImageError(true);
                                        img.src = imageUrl;
                                    }}
                                />
                            </Box>
                        ) : !imageLoaded ? (
                            <CircularProgress sx={{ color: '#90caf9' }} />
                        ) : (
                            /* Canvas rendered directly — zoom/pan is handled inside the canvas context, not CSS */
                            <canvas
                                ref={canvasRef}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseUp}
                                onDoubleClick={handleDoubleClick}
                                style={{
                                    cursor: getCanvasCursor(),
                                    borderRadius: '8px',
                                }}
                            />
                        )}
                        {/* Hidden mask canvas for generating final mask at original resolution */}
                        <canvas ref={maskCanvasRef} style={{ display: 'none' }} />

                        {isLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    borderRadius: 2,
                                }}
                            >
                                <Box sx={{ textAlign: 'center' }}>
                                    <CircularProgress sx={{ color: '#90caf9' }} />
                                    <Typography sx={{ mt: 1, color: 'rgba(255,255,255,0.8)' }}>
                                        {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.generating')}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* Vertical Zoom Slider */}
                    {imageLoaded && !imageError && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 0.5,
                                py: 2,
                                gap: 1,
                                bgcolor: 'rgba(255,255,255,0.06)',
                                borderRadius: 2,
                                minWidth: 40,
                            }}
                        >
                            <Tooltip
                                title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.zoomIn')}
                                placement="left"
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => setZoom((prev) => Math.min(5, +(prev + 0.25).toFixed(2)))}
                                    sx={{ color: 'rgba(255,255,255,0.6)', p: 0.5 }}
                                >
                                    <ZoomIn fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Slider
                                orientation="vertical"
                                value={zoom}
                                onChange={(_, val) => setZoom(val as number)}
                                min={1}
                                max={5}
                                step={0.1}
                                sx={{
                                    height: '100%',
                                    minHeight: 120,
                                    color: '#90caf9',
                                    '& .MuiSlider-thumb': { width: 14, height: 14 },
                                    '& .MuiSlider-track': { width: 4 },
                                    '& .MuiSlider-rail': { width: 4, opacity: 0.3 },
                                }}
                            />
                            <Tooltip
                                title={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.zoomOut')}
                                placement="left"
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => setZoom((prev) => Math.max(1, +(prev - 0.25).toFixed(2)))}
                                    sx={{ color: 'rgba(255,255,255,0.6)', p: 0.5 }}
                                >
                                    <ZoomOut fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: '#90caf9',
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    mt: 0.5,
                                }}
                            >
                                {Math.round(zoom * 100)}%
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Reference Image + Prompt Row */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
                    {/* Reference Image Upload */}
                    <Box
                        sx={{
                            minWidth: 140,
                            maxWidth: 180,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'rgba(255,255,255,0.7)',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <ImageIcon sx={{ fontSize: 14 }} />
                            {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.referenceImage')}
                            <Typography component="span" sx={{ color: '#ff8a80', fontSize: '11px' }}>
                                *
                            </Typography>
                        </Typography>
                        <input
                            ref={referenceInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            hidden
                            onChange={handleReferenceImageUpload}
                        />
                        <Box
                            onClick={() => !isLoading && referenceInputRef.current?.click()}
                            sx={{
                                flex: 1,
                                minHeight: 80,
                                border: referenceImage
                                    ? '2px solid rgba(144, 202, 249, 0.6)'
                                    : '2px dashed rgba(255,255,255,0.25)',
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                overflow: 'hidden',
                                position: 'relative',
                                bgcolor: 'rgba(255,255,255,0.04)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: isLoading ? undefined : 'rgba(144, 202, 249, 0.5)',
                                    bgcolor: isLoading ? undefined : 'rgba(255,255,255,0.08)',
                                },
                            }}
                        >
                            {referenceImage ? (
                                <>
                                    <img
                                        src={referenceImage.preview}
                                        alt="Reference"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: '6px',
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setReferenceImage(null);
                                        }}
                                        disabled={isLoading}
                                        sx={{
                                            position: 'absolute',
                                            top: 2,
                                            right: 2,
                                            bgcolor: 'rgba(0,0,0,0.6)',
                                            color: 'white',
                                            width: 20,
                                            height: 20,
                                            '&:hover': { bgcolor: 'rgba(220,53,69,0.8)' },
                                        }}
                                    >
                                        <Close sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </>
                            ) : (
                                <Box sx={{ textAlign: 'center', p: 1 }}>
                                    <AddPhotoAlternate sx={{ fontSize: 28, color: 'rgba(255,255,255,0.3)' }} />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            color: 'rgba(255,255,255,0.35)',
                                            fontSize: '10px',
                                            mt: 0.5,
                                        }}
                                    >
                                        {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.addReference')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Prompt Input */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                            {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.promptLabel')}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={4}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder={t(
                                'mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.promptPlaceholder'
                            )}
                            disabled={isLoading}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    color: 'white',
                                    bgcolor: 'rgba(255,255,255,0.06)',
                                    height: '100%',
                                    alignItems: 'flex-start',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                                    '&.Mui-focused fieldset': { borderColor: '#90caf9' },
                                },
                                '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(255,255,255,0.4)',
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* Validation hint */}
                {!hasReference && hasMask && (
                    <Alert severity="warning" sx={{ py: 0.5, bgcolor: 'rgba(255,152,0,0.1)', color: '#ffb74d' }}>
                        {t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.referenceRequired')}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    p: 2,
                    gap: 1,
                }}
            >
                <SCUIButton
                    type="secondary"
                    size="m"
                    sizeType="default"
                    text={t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.cancel')}
                    onClick={onClose}
                    disabled={isLoading}
                />
                <SCUIButton
                    type="primary"
                    size="m"
                    sizeType="default"
                    text={
                        isLoading
                            ? t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.generating')
                            : t('mannequin.aiGeneration.hangerFlow.uploadGenerate.inpainting.generate')
                    }
                    onClick={handleGenerate}
                    disabled={!hasMask || !hasReference || isLoading}
                />
            </DialogActions>
        </Dialog>
    );
};
